// src/components/pages/Pessoa/modules/usePessoaNomeModule.js

import { useCallback, useEffect, useMemo, useState } from 'react';
import * as PessoaAPI from '../../../../api/pessoa';

function createNomeDraft(pessoaId = null) {
    return {
        pessoaId: pessoaId || null,
        uuid: '',
        nome: '',
        origem: '',
        data: '',
        ativo: false,
        funcao: 'Adicionar',
    };
}

export function usePessoaNomeModule({ pessoaId, rows = [], setRows, setFlashMessage }) {
    const [draft, setDraft] = useState(createNomeDraft(pessoaId));
    const [editingIndex, setEditingIndex] = useState(null);

    useEffect(() => {
        setDraft((prev) => ({
            ...prev,
            pessoaId: pessoaId || null,
        }));
    }, [pessoaId]);

    const resetDraft = useCallback(() => {
        setDraft(createNomeDraft(pessoaId));
        setEditingIndex(null);
    }, [pessoaId]);

    const handleEdit = useCallback(
        (row, index) => {
            setDraft({
                index,
                pessoaId: row.pessoaId || pessoaId || null,
                uuid: row.uuid || '',
                nome: row.nome || '',
                origem: row.origem || '',
                data: row.data || '',
                ativo: !!row.ativo,
                funcao: 'Editar',
            });
            setEditingIndex(index);
        },
        [pessoaId]
    );

    const handleDelete = useCallback(
        async (row, index) => {
            const payload = {
                index,
                pessoaId: row.pessoaId || pessoaId || null,
                uuid: row.uuid || '',
                nome: row.nome || '',
                origem: row.origem || '',
                data: row.data || '',
                ativo: !!row.ativo,
                funcao: 'deletar',
            };

            if (!pessoaId) {
                setRows((prev) => prev.filter((_, i) => i !== index));
                setFlashMessage?.('Nome removido da lista.', 'success');
                if (editingIndex === index) resetDraft();
                return;
            }

            try {
                const result = await PessoaAPI.nomes(payload);

                if (result?.data?.type === 'success') {
                    setRows(result.data.resultado || []);
                }

                setFlashMessage?.(result?.data?.message || 'Operação realizada.', result?.data?.type || 'success');

                if (editingIndex === index) resetDraft();
            } catch (error) {
                console.error(error);
                setFlashMessage?.(error?.response?.data?.message || 'Erro ao excluir nome.', 'error');
            }
        },
        [pessoaId, setRows, setFlashMessage, editingIndex, resetDraft]
    );

    const handleSave = useCallback(async () => {
        if (!draft.nome?.trim()) {
            setFlashMessage?.('Digite o nome.', 'warning');
            return;
        }

        const payload = {
            ...draft,
            pessoaId: pessoaId || draft.pessoaId || null,
        };

        if (!pessoaId) {
            if (String(payload.funcao).toLowerCase() === 'editar' && editingIndex !== null) {
                setRows((prev) =>
                    prev.map((item, index) =>
                        index === editingIndex
                            ? {
                                  ...item,
                                  ...payload,
                              }
                            : item
                    )
                );
                setFlashMessage?.('Nome atualizado na lista.', 'success');
            } else {
                setRows((prev) => [
                    ...prev,
                    {
                        ...payload,
                        uuid: payload.uuid || `draft-nome-${Date.now()}`,
                    },
                ]);
                setFlashMessage?.('Nome adicionado na lista.', 'success');
            }

            resetDraft();
            return;
        }

        try {
            const result = await PessoaAPI.nomes(payload);

            if (result?.data?.type === 'success') {
                setRows(result.data.resultado || []);
            }

            setFlashMessage?.(result?.data?.message || 'Operação realizada.', result?.data?.type || 'success');
            resetDraft();
        } catch (error) {
            console.error(error);
            setFlashMessage?.(error?.response?.data?.message || 'Erro ao salvar nome.', 'error');
        }
    }, [draft, pessoaId, editingIndex, resetDraft, setRows, setFlashMessage]);

    const columns = useMemo(
        () => [
            {
                id: 'nomes',
                header: 'Nomes da pessoa',
                columns: [
                    {
                        accessorKey: 'nome',
                        header: 'Nome',
                        size: 260,
                    },
                    {
                        accessorKey: 'origem',
                        header: 'Origem',
                        size: 120,
                    },
                    {
                        accessorKey: 'data',
                        header: 'Data',
                        size: 120,
                    },
                    {
                        accessorKey: 'ativo',
                        header: 'Ativo',
                        size: 80,
                        Cell: ({ row }) => (row.original.ativo ? 'Sim' : 'Não'),
                    },
                ],
            },
        ],
        []
    );

    return {
        state: {
            items: rows || [],
            rowCount: rows?.length || 0,
            isLoading: false,
            isRefetching: false,
            isError: false,
            novoNome: draft,
        },
        actions: {
            setNovoNome: setDraft,
            limpar: resetDraft,
            submit: handleSave,
            handleEditarOuDeletar: (row, index, funcao) => {
                if (String(funcao).toLowerCase() === 'editar') {
                    handleEdit(row, index);
                    return;
                }
                handleDelete(row, index);
            },
            change: (e) => {
                const { name, value, checked, type, options, selectedIndex } = e.target;
                const field = name.split('.')[1];

                setDraft((prev) => ({
                    ...prev,
                    [field]: field === 'origem' ? options?.[selectedIndex]?.text || value : type === 'checkbox' ? checked : value,
                }));
            },
            focus: () => {},
            blur: () => {},
        },
        columns,
    };
}
